gsap.registerPlugin(CustomEase)

CustomEase.create('smooth', '0.77,0,0.175,1')

class BundleDrawer extends HTMLElement {
    constructor() {
        super()

        this.isOpen = false
        this.isLoading = false

        this.modalId = this.dataset.modal
        this.backdrop = this.querySelector('[data-modal="bg"]')
        this.content = this.querySelector('[data-modal="content"]')
        this.replaceTarget = this.querySelector('[data-replace-inner-html]')
        this.closeBtn = this.querySelector('[data-modal="close"]')

        this.openButtons = document.querySelectorAll('[data-bundle-builder-trigger]')

        this.openButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.handleTrigger(button)
            })
        })

        this.timeline = null
    }

    connectedCallback() {
        this.setAttribute('aria-hidden', 'true')
        this.hidden = true

        this.createTimeline()

        this.closeBtn?.addEventListener('click', () => this.close())
        this.backdrop?.addEventListener('click', () => this.close())
    }

    createTimeline() {
        this.timeline = gsap.timeline({
            paused: true,
            defaults: {
                duration: 0.6,
                ease: 'smooth',
            },
            onReverseComplete: () => {
                this.hidden = true
                this.setAttribute('aria-hidden', 'true')
            },
        })

        this.timeline
            .set(this, { display: 'flex' })
            .from(this.content, { x: '100%' })
            .fromTo(this.backdrop, { opacity: 0 }, { opacity: 0.3 }, 0)
            // Mobile draws the dim as a shadow of the panel instead (see theme.liquid)
            .fromTo(this, { '--modal-dim': 0 }, { '--modal-dim': 1 }, 0)
    }

    async handleTrigger(button) {
        if (this.isLoading) return
        this.isLoading = true

        const url = button.dataset.url

        // Touch screens: open at least 150ms after the tap, so the tap state on the eye button
        // is visible before the overlay dims the page (the content loads in the meantime)
        const minDelay = window.matchMedia('(hover: none)').matches
            ? new Promise(resolve => setTimeout(resolve, 150))
            : null

        if (url) {
            await this.fetchAndReplace(url)
        }
        if (minDelay) await minDelay

        this.syncPriceFromBuilder()

        this.isLoading = false
        this.open()
    }

    syncPriceFromBuilder() {
        const builder = document.querySelector('bundle-builder')
        if (!builder || typeof builder.getPricingState !== 'function') return

        const pricing = builder.getPricingState()

        const elDiscounted = this.querySelector('[data-next-discounted-price]')
        const elOriginal = this.querySelector('[data-original-price]')

        if (!elDiscounted || !elOriginal) return

        const formatMoney = (cents) => {
            if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                return Shopify.formatMoney(cents)
            }
            return ''
        }


        if (!pricing.subscriptionSelected) {
            elDiscounted.textContent = formatMoney(pricing.singlePackPriceCents)
            elOriginal.hidden = true
            return
        }

        const discounted = pricing.nextDiscountedUnitPrice
        const original = pricing.singlePackPriceCents

        if (pricing.nextDiscount > 0) {
            elDiscounted.textContent = formatMoney(discounted)
            elOriginal.textContent = formatMoney(original)
            elOriginal.hidden = false
        } else {
            elDiscounted.textContent = formatMoney(original)
            elOriginal.hidden = true
        }
    }




    async fetchAndReplace(url) {
        document.dispatchEvent(new Event('loading:start'));
        try {
            const response = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
            const html = await response.text()

            const parser = new DOMParser()
            const doc = parser.parseFromString(html, 'text/html')

            const newContent = doc.querySelector('bundle-drawer-content')

            if (newContent && this.replaceTarget) {
                this.replaceTarget.replaceWith(newContent)

                // update internal references
                this.replaceTarget = newContent
                this.content = newContent

                // rebuild animation with new element
                this.timeline.kill()
                this.createTimeline()
            }

        } catch (error) {
            console.error(error)
        }
        document.dispatchEvent(new Event('loading:end'));
    }

    open() {
        if (this.isOpen) return
        this.isOpen = true

        this.hidden = false
        this.setAttribute('aria-hidden', 'false')
        document.body.classList.add('overflow-hidden')

        this.timeline.play()
        const content = this.querySelector('bundle-drawer-content')
        content?.handleModalOpened(1000)
    }

    close() {
        if (!this.isOpen) return
        this.isOpen = false

        document.body.classList.remove('overflow-hidden')
        this.timeline.reverse()
    }
}

customElements.define('bundle-drawer', BundleDrawer)


class BundleDrawerContent extends HTMLElement {
    constructor() {
        super()

        this.swiper = null
        this.addButton = null
        this.closeButton = null
        this._videoPlayTimeout = null
        this._nonActiveResetTimeouts = []
        this.firstVideoIndex = -1

        this.handleAddClick = this.handleAddClick.bind(this)
    }

    connectedCallback() {
        this.addButton = this.querySelector('[data-add-bundle]')
        this.addButton?.addEventListener('click', this.handleAddClick)
        this.closeButton = this.querySelector('[data-modal="close"]')
        this.closeButton?.addEventListener('click', () => this.closest('bundle-drawer')?.close())
        this.initSwiper()
    }

    disconnectedCallback() {
        this.addButton?.removeEventListener('click', this.handleAddClick)
        this.destroySwiper()
    }

    _clearVideoTimeout() {
        if (this._videoPlayTimeout) {
            clearTimeout(this._videoPlayTimeout)
            this._videoPlayTimeout = null
        }
    }

    _clearNonActiveResetTimeouts() {
        this._nonActiveResetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
        this._nonActiveResetTimeouts = []
    }

    _getVideoInSlide(slideEl) {
        return slideEl ? slideEl.querySelector('video') : null
    }

    _pauseAndResetVideo(slideEl) {
        const video = this._getVideoInSlide(slideEl)
        if (!video) return
        video.pause()
        video.currentTime = 0
    }

    _playVideoOnce(slideEl, delayMs = 0) {
        const video = this._getVideoInSlide(slideEl)
        if (!video) return

        video.loop = false
        video.muted = true
        video.playsInline = true
        video.preload = 'metadata'

        const play = () => {
            video.currentTime = 0
            if (video.readyState >= 2) {
                video.play().catch(() => { })
                return
            }
            video.load()
            video.addEventListener('canplay', () => {
                video.currentTime = 0
                video.play().catch(() => { })
            }, { once: true })
        }

        if (delayMs > 0) {
            this._clearVideoTimeout()
            this._videoPlayTimeout = setTimeout(play, delayMs)
        } else {
            play()
        }
    }

    _scheduleInactiveSlideResets(activeIndex = this.swiper?.activeIndex) {
        if (!this.swiper) return
        const NON_ACTIVE_RESET_DELAY_MS = 600
        this._clearNonActiveResetTimeouts()
        this.swiper.slides.forEach((slide, index) => {
            if (index === activeIndex) return
            const timeoutId = setTimeout(() => {
                this._pauseAndResetVideo(slide)
            }, NON_ACTIVE_RESET_DELAY_MS)
            this._nonActiveResetTimeouts.push(timeoutId)
        })
    }

    _onSlideChangeStart(swiper) {
        this._scheduleInactiveSlideResets(swiper.activeIndex)
    }

    _onSlideChangeEnd(swiper) {
        this._clearVideoTimeout()
        this._playVideoOnce(swiper.slides[swiper.activeIndex], 120)
    }

    playActiveSlideVideo(delayMs = 0) {
        if (!this.swiper) return
        this._scheduleInactiveSlideResets()
        this._playVideoOnce(this.swiper.slides[this.swiper.activeIndex], delayMs)
    }

    playSlideVideoByIndex(index, delayMs = 0) {
        if (!this.swiper) return
        this._scheduleInactiveSlideResets(index)
        this._playVideoOnce(this.swiper.slides[index], delayMs)
    }

    handleModalOpened(delayMs = 1000) {
        if (!this.swiper) return

        this.swiper.update()
        const hasVideoInActiveSlide = !!this._getVideoInSlide(this.swiper.slides[this.swiper.activeIndex])
        const targetIndex = hasVideoInActiveSlide
            ? this.swiper.activeIndex
            : (this.firstVideoIndex >= 0 ? this.firstVideoIndex : this.swiper.activeIndex)

        this.swiper.slideTo(targetIndex, 0, false)

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.playActiveSlideVideo(delayMs)
            })
        })
    }

    handleAddClick() {
        const variantId = this.addButton?.getAttribute('data-variant-id')
        if (!variantId) return

        const builder = document.querySelector('bundle-builder')
        if (!builder) return

        const input = builder.querySelector(
            `[data-bundle-quantity][data-variant-id="${variantId}"]`
        )

        if (!input) return

        const current = parseInt(input.value || 0, 10)
        input.value = current + 1

        input.dispatchEvent(new Event('change', { bubbles: true }))

        this.closest('bundle-drawer')?.close()
    }

    initSwiper() {
        const container = this.querySelector('[data-swiper="bundle-builder-media"]')
        if (!container) return

        const prevBtn = this.querySelector('[data-swiper-button="prev"]')
        const nextBtn = this.querySelector('[data-swiper-button="next"]')
        const slides = Array.from(container.querySelectorAll('.swiper-slide'))
        this.firstVideoIndex = slides.findIndex((slide) => this._getVideoInSlide(slide))

        this.swiper = new Swiper(container, {
            initialSlide: this.firstVideoIndex >= 0 ? this.firstVideoIndex : 0,
            slidesPerView: 'auto',
            speed: 1000,
            spaceBetween: 8,
            navigation: {
                prevEl: prevBtn,
                nextEl: nextBtn,
                disabledClass: 'opacity-30 cursor-not-allowed',
            },
            keyboard: { enabled: true },
            a11y: { enabled: true },
            observer: true,
            observeParents: true,
            on: {
                init: (swiper) => {
                    swiper.slides.forEach((slide) => this._pauseAndResetVideo(slide))
                    swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)'
                    const activeSlide = swiper.slides[swiper.activeIndex]
                    this._playVideoOnce(activeSlide, 150)
                },
                setTransition: (swiper) => {
                    swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)'
                },
                slideChange: (swiper) => {
                    this._onSlideChangeStart(swiper)
                },
                slideChangeTransitionEnd: (swiper) => {
                    this._onSlideChangeEnd(swiper)
                },
            },
        })
    }

    destroySwiper() {
        this._clearVideoTimeout()
        this._clearNonActiveResetTimeouts()
        if (!this.swiper) return
        this.swiper.destroy(true, true)
        this.swiper = null
    }
}

customElements.define('bundle-drawer-content', BundleDrawerContent)
